import { useState, useEffect } from "react";
import { Box, Wizard } from "@cloudscape-design/components";
import { useBreadcrumbs } from "contexts/BreadcrumbContext";
import { useNotification } from "contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import { DeploymentData, initialDeploymentState } from "types/deploymentTypes";
import { prepareDedicatedDeploymentPayload } from "utils/Utils";
import { validateDeploymentData, validateInvoiceData } from "utils/validationUtils";
import AppConfig from "configs/AppConfig";
import ClusterDetailsStep from "./ClusterDetailsStep";
import ClusterInvoiceStep from "./ClusterInvoiceStep";
import organizationService from "services/organizationService";
import { initialInvoice, InvoiceData } from "types/invoiceTypes";
import { OrganizationOption } from "types/organizationTypes";
import deploymentService from "services/deploymentService";
import { useCluster } from "contexts/ClusterContext";
import { buildOrderSummary } from "utils/invoiceUtils";
import NEW_PLANS from "configs/newPlans.json";
import { clusterSizes, securityAddOnsList } from "configs/settings";


const serviceAgreementStatement = (
    <>
        By creating a Dedicated Deployment, you agree to be bound by the terms of
        our <a href={AppConfig.SERVICE_AGREEMENT_URL} target="_blank" rel="noopener noreferrer">
            Service Agreement
        </a>. This will create a dedicated
        deployment that will not share resources with other deployments.
    </>
);


const CreateDedicatedClusterPage = () => {

    // Wizard Step/Section Index
    const [activeStepIndex, setActiveStepIndex] = useState<number>(0);

    const { addNotification } = useNotification();
    const { setBreadcrumbs } = useBreadcrumbs();

    const navigate = useNavigate();

    // Organizations
    const [organizationOptions, setOrganizationOptions] = useState<OrganizationOption[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [organizationError, setOrganizationError] = useState<string | null>(null);
    const [hasUsedFreeTrial, setHasUsedFreeTrial] = useState<boolean>(true);

    // Cluster
    const [deploymentData, setDeploymentData] = useState<DeploymentData>(initialDeploymentState);
    const [isCreating, setIsCreating] = useState(false);
    const [isValidating, setIsValidating] = useState(false);

    // Invoice Informations
    const [invoiceData, setInvoiceData] = useState<InvoiceData>(initialInvoice);

    // SLA Checkbox
    const [slaCheckbox, setSlaCheckbox] = useState<boolean>(false);
    const [slaCheckboxError, setSlaCheckboxError] = useState<undefined | string>(undefined);

    // Flag for custom domain
    const [useCustomDomain, setUseCustomDomain] = useState<boolean>(false);

    const { fetchClusters } = useCluster();

    // Security add-ons
    const [securityAddOns, setSecurityAddOns] = useState<string[]>([]);

    // Order summary data
   const orderSummary = buildOrderSummary(deploymentData, securityAddOns, hasUsedFreeTrial);

    // Selected Plan
    const selectedPlan = NEW_PLANS.find((plan) => plan.name.toLowerCase() === deploymentData.deploymentPlan);

    // Selected Cluster Size
    const selectedSize = (clusterSizes.find((size) => size === deploymentData.clusterSize) ?? "small") as keyof NonNullable<typeof selectedPlan>["size"];

    // Reset security add-ons and custom domain when switching to an unsupported plan
    useEffect(() => {
        if (deploymentData.deploymentPlan === "develop") {
            setDeploymentData((prev) => {
            const updatedData = { ...prev };

            securityAddOns.forEach((addOnId) => {
                const addOn = securityAddOnsList.find(
                    (item) => item.id === addOnId
                );

                if (addOn) {
                    (updatedData[addOn.deploymentProperty as keyof typeof updatedData] as any) = false;
                }
            });

            return updatedData;
            });

            setSecurityAddOns([]);

            setUseCustomDomain(false);
        }
    }, [deploymentData.deploymentPlan]);


    // Create Cluster Handler
    const handleCreate = async () => {
         if (typeof (window as any).gtag_report_conversion === "function") {
    (window as any).gtag_report_conversion();
  }
        try {
            setIsCreating(true);

            // Validate Invoice Data
            const invoiceValidation = validateInvoiceData(invoiceData);

            if(!invoiceValidation.isValid){
                setIsCreating(false);
                setInvoiceData((prev)=>({
                    ...prev,
                    errors: invoiceValidation.errors
                }));
                return;
            }

            // Validate that SLA checkbox is selected
            if(!slaCheckbox){
                setIsCreating(false);
                setSlaCheckboxError("Please check this box to accept the SLA");
                return;
            }
            
            // No need to validate cluster info again
            const { validData } = validateDeploymentData(deploymentData, "dedicated");   
            
            // Invoice Details Object
            const invoiceDetailsObject = {
                first_name: invoiceData.firstName,
                last_name: invoiceData.lastName,
                billing_email: invoiceData.billingEmail,
                phone: invoiceData.phoneNumber,
                company_name: invoiceData.company,
                billing_address: invoiceData.billingAddress,
                city: invoiceData.city,
                postcode: invoiceData.postalCode,
                price: invoiceData.price,
                due_date: invoiceData.dueDate
            }

            // API Payload
            const payload = prepareDedicatedDeploymentPayload({
                plan: validData.deploymentPlan ?? "",
                billingCycle: validData.billingCycle ?? "",
                region: validData.region,
                name: validData.name,
                organizationId: validData.organizationId,
                domainName: useCustomDomain ? validData.domainName : `${validData.domainName}.authn-z.link`,
                hostedZoneId: validData.hostedZoneId,
                subscriptionId: validData.subscriptionId,
                invoiceDetailsObject,
                selectedKcVersion: validData.keycloakVersion,
                size: validData.clusterSize,
                useAlb: validData.useAlb
            });
            
            // Create cluster
            await deploymentService.handleCreateCluster(payload);

            fetchClusters(); // Refresh the cluster list to update the cluster selector

            if(useCustomDomain){
                // Mark domain as used
                await organizationService.updateDomain(validData.organizationId, validData.domainName);
            }
            
            // to remove in the future: update the free-trial to true after first use
            await organizationService.updateFreeTrial(validData.organizationId, true);
            
            addNotification("success", "Your cluster creation is pending! You will get notified once it is up (between 3-5 min)."); 
            navigate("/dedicated-clusters");

        } catch (error: any) {
            console.error("Failed to create cluster. Please try again.", error);

            if (error.response?.data?.errors) {
                const apiErrors = error.response.data.errors;
                const newErrors: Record<string, string> = {};
                apiErrors.forEach((apiError: any) => {
                    if (apiError.code === "UNIQUE_CONSTRAINT_VIOLATION") {
                        // Map pointer to form field
                        if (apiError.source?.pointer === "/data/attributes/name") {
                            newErrors.name = "This name is already in use, please choose another one.";
                        } else if (apiError.source?.pointer === "/data/attributes/domain_name") {
                            newErrors.domainName = "This domain is already in use, please choose another one.";
                        }
                    }
                });

                // Set the errors in the form state
                setDeploymentData((prev) => ({
                    ...prev,
                    errors: { ...prev.errors, ...newErrors },
                }));
                
                setActiveStepIndex(0); // Fallback
            }

            addNotification("error", "Failed to create cluster. Please try again.");
        } 
        finally {
            setIsCreating(false);
        }
    };
    
    // Validates cluster form fields on the client
    const validateClusterFormFields = ()=>{
        const validation = validateDeploymentData(deploymentData, "dedicated");
        if(!validation.isValid){
            setDeploymentData((prev) => ({
                ...prev,
                errors: validation.errors || {},
            }));
        }
        return validation.isValid;
    }

    // Validates cluster uniqueness via backend API
    const validateClusterUniqueness = async () => {
        let isValid = false;

        try {
            setIsValidating(true);

            const response = await deploymentService.validateClusterInformations(deploymentData, useCustomDomain);

            isValid = response.isValid;

            if (response.errors.length > 0) {
                const apiErrors = response.errors;
                const newErrors: Record<string, string> = {};
                apiErrors.forEach((apiError: any) => {
                    if (apiError.code === "UNIQUE_CONSTRAINT_VIOLATION") {
                        // Map pointer to form field
                        if (apiError.source?.pointer === "/data/attributes/name") {
                            newErrors.name = "This name is already in use, please choose another one.";
                        } else if (apiError.source?.pointer === "/data/attributes/domain_name") {
                            newErrors.domainName = "This domain is already in use, please choose another one.";
                        }
                    }
                });

                // Set the errors in the form state
                setDeploymentData((prev) => ({
                    ...prev,
                    errors: { ...prev.errors, ...newErrors },
                }));
            }

        } catch (error) {
            console.error(error);
            addNotification("error", "Server validation failed. Please try again");
        } finally {
            setIsValidating(false);
            return isValid;
        }
    }

    // Fetch Organizations
    const fetchOrganizationOptions = async () => {
        try {
            setLoadingOrganizations(true);
            setOrganizationError(null);
            const data = await organizationService.getOrganizationOptions();

            if (!data || data.length === 0) {
                setOrganizationError("No organizations found. Please create an organization first.");
                setOrganizationOptions([]);
                return;
            }

            setOrganizationOptions(data);
            setHasUsedFreeTrial(data[0]?.free_trial ?? true);
        } catch (error) {
            console.error("Failed to load organizations. Please try again later.", error);
            setOrganizationError("Failed to load organizations. Please try again later.");
            addNotification("error", "Failed to load organizations. Please try again later.");
        } finally {
            setLoadingOrganizations(false);
        }
    };


    useEffect(() => {
        fetchOrganizationOptions();
    }, []);

    useEffect(() => {
        setBreadcrumbs([
            { text: AppConfig.APP_NAME, href: "/" },
            { text: "Dedicated Clusters", href: "/dedicated-clusters" },
            { text: "Create", href: "/dedicated-clusters/create" }, // 👈 breadcrumb text "Create"
        ]);

        return () => {
            setBreadcrumbs([]);
        };
    }, [setBreadcrumbs]);


    return (
        <Box className="wizard-wrapper" margin={{bottom:"xxxl"}}> {/* Bottom spacing is added to prevent chatbot widget from overlapping wizard actions*/}
            <Wizard
                i18nStrings={{
                    stepNumberLabel: stepNumber =>
                    `Step ${stepNumber}`,
                    collapsedStepsLabel: (stepNumber, stepsCount) =>
                    `Step ${stepNumber} of ${stepsCount}`,
                    navigationAriaLabel: "Steps",
                    cancelButton: "Cancel",
                    previousButton: "Previous",
                    nextButton: "Next",
                    optional: "optional"
                }}
                onNavigate={async ({ detail }) =>{
                    
                    // Validates cluster data before allowing navigation to the next step
                    const moveStep = async (requestedStepIndex: number) => {
                        
                        const isFormValid = validateClusterFormFields();
                        if (!isFormValid) return;

                        const isUniquenessValid = await validateClusterUniqueness();
                        if (!isUniquenessValid) return;

                        setActiveStepIndex(requestedStepIndex);
                    };

                    switch(detail.reason){
                        case "next":
                            moveStep(detail.requestedStepIndex);
                            break;
    
                        case "previous":
                            setActiveStepIndex(detail.requestedStepIndex)
                            break;
                        
                        case "step":
                            // Index 1 refers to invoice details section 
                            if(detail.requestedStepIndex === 1) moveStep(detail.requestedStepIndex);
                            else setActiveStepIndex(detail.requestedStepIndex);

                            break;
    
                        default: break;
                    }
                }}
                activeStepIndex={activeStepIndex}
                submitButtonText="Confirm Order, and Generate Invoice"
                steps={[
                    {
                        title: "Configure Cluster",
                        description: serviceAgreementStatement,
                        content: 
                            <ClusterDetailsStep 
                                useCustomDomain={useCustomDomain} 
                                setUseCustomDomain={setUseCustomDomain} 
                                deploymentData={deploymentData} 
                                setDeploymentData={setDeploymentData} 
                                loadingOrganizations={loadingOrganizations}
                                organizationError={organizationError}
                                organizationOptions={organizationOptions}
                                hasUsedFreeTrial={hasUsedFreeTrial}
                                securityAddOns={securityAddOns}
                                setSecurityAddOns={setSecurityAddOns}
                                orderSummary={orderSummary}
                                selectedPlan={selectedPlan}
                                selectedSize={selectedSize}
                            />
                    },
                    {
                        title: "Setup Invoice Details",
                        description: "Finalize your infrastructure setup and invoice details below.",
                        content: 
                            <ClusterInvoiceStep 
                                invoiceData={invoiceData} 
                                setInvoiceData={setInvoiceData} 
                                slaCheckbox={slaCheckbox} 
                                setSlaCheckbox={setSlaCheckbox} 
                                deploymentData={deploymentData} 
                                slaCheckboxError={slaCheckboxError} 
                                setSlaCheckboxError={setSlaCheckboxError} 
                                hasUsedFreeTrial={hasUsedFreeTrial}
                                securityAddOns={securityAddOns}
                                orderSummary={orderSummary}
                                selectedPlan={selectedPlan}
                                selectedSize={selectedSize}
                            />
                    }
                ]}
                onCancel={()=>{navigate("/dedicated-clusters")}}
                onSubmit={handleCreate}
                isLoadingNextStep={isValidating || isCreating}
            />
        </Box>
    );
};

export default CreateDedicatedClusterPage;